/**
 * TimecodeConverter - Utilities for converting between time formats
 * 
 * Provides conversion functions for:
 * - Seconds to SMPTE timecode (HH:MM:SS:FF)
 * - Seconds to FCPXML duration format
 * - Frame-accurate timing calculations
 * - Drop-frame and non-drop-frame timecode support
 */
const TimecodeConverter = {

    /**
     * Convert seconds to SMPTE timecode format (HH:MM:SS:FF)
     * @param {Number} seconds - Time in seconds (can be fractional)
     * @param {Number} frameRate - Target frame rate (23.976, 24, 25, 29.97, 30, 60)
     * @param {Boolean} dropFrame - Use drop-frame timecode (for 29.97 fps)
     * @returns {String} Timecode in HH:MM:SS:FF format
     */
    convertSecondsToTimecode: function(seconds, frameRate, dropFrame = false) {
        frameRate = frameRate || 25;
        
        // Handle negative time
        if (seconds < 0) {
            return "00:00:00:00";
        }
        
        // Convert to total frames
        let totalFrames = Math.round(seconds * frameRate);
        
        // Handle drop-frame timecode for 29.97 fps
        if (dropFrame && (frameRate === 29.97 || frameRate === 59.94)) {
            return this._convertFramesToDropFrameTimecode(totalFrames, frameRate);
        }
        
        // Non-drop-frame calculation
        const frames = totalFrames % frameRate;
        const totalSeconds = Math.floor(totalFrames / frameRate);
        const minutes = Math.floor(totalSeconds / 60);
        const hours = Math.floor(minutes / 60);
        
        const hoursDisplay = hours % 24;
        const minutesDisplay = minutes % 60;
        const secondsDisplay = totalSeconds % 60;
        
        // Format with zero padding
        return [
            hoursDisplay.toString().padStart(2, '0'),
            minutesDisplay.toString().padStart(2, '0'),
            secondsDisplay.toString().padStart(2, '0'),
            Math.floor(frames).toString().padStart(2, '0')
        ].join(':');
    },

    /**
     * Convert frames to drop-frame timecode
     * @private
     * @param {Number} totalFrames - Total frame count
     * @param {Number} frameRate - Frame rate (29.97 or 59.94)
     * @returns {String} Drop-frame timecode
     */
    _convertFramesToDropFrameTimecode: function(totalFrames, frameRate) {
        const dropFrames = frameRate === 59.94 ? 4 : 2;
        const nominalFrameRate = Math.round(frameRate); // 30 for 29.97, 60 for 59.94
        
        // Drop frame rules: frames are dropped at the start of every minute EXCEPT every 10th minute
        // So in 10 minutes: minutes 1,2,3,4,5,6,7,8,9 drop frames, but minute 0,10,20,30,etc. do NOT
        const framesPerNormalMinute = nominalFrameRate * 60 - dropFrames; // e.g., 1798 for 29.97fps
        const framesPerTenthMinute = nominalFrameRate * 60; // e.g., 1800 for 29.97fps (no drop)
        const framesPer10Minutes = (framesPerNormalMinute * 9) + framesPerTenthMinute;
        
        // Calculate complete 10-minute groups
        const complete10MinGroups = Math.floor(totalFrames / framesPer10Minutes);
        let remainingFrames = totalFrames % framesPer10Minutes;
        
        // Calculate hours from complete 10-minute groups
        const hours = Math.floor(complete10MinGroups / 6);
        const remaining10MinGroupsInHour = complete10MinGroups % 6;
        
        // Calculate minutes within the current 10-minute group
        let minutesInGroup = 0;
        
        // Process each minute within the 10-minute group
        for (let min = 0; min < 10 && remainingFrames > 0; min++) {
            const isDropMinute = (min % 10) !== 0; // Minutes 1-9 drop frames, minute 0 doesn't
            const framesThisMinute = isDropMinute ? framesPerNormalMinute : framesPerTenthMinute;
            
            if (remainingFrames >= framesThisMinute) {
                remainingFrames -= framesThisMinute;
                minutesInGroup++;
            } else {
                break;
            }
        }
        
        // Calculate final minutes, seconds, and frames
        const totalMinutes = (remaining10MinGroupsInHour * 10) + minutesInGroup;
        const seconds = Math.floor(remainingFrames / nominalFrameRate);
        const frames = remainingFrames % nominalFrameRate;
        
        return [
            hours.toString().padStart(2, '0'),
            totalMinutes.toString().padStart(2, '0'),
            seconds.toString().padStart(2, '0'),
            frames.toString().padStart(2, '0')
        ].join(':');
    },

    /**
     * Convert seconds to FCPXML duration format (e.g., "100/25s" for 4 seconds at 25fps)
     * @param {Number} seconds - Time in seconds
     * @param {Number} frameRate - Frame rate for calculation
     * @returns {String} FCPXML duration string
     */
    convertSecondsToFCPXMLDuration: function(seconds, frameRate) {
        frameRate = frameRate || 25;
        const totalFrames = Math.round(seconds * frameRate);
        const fractionalFrameRateMap = {
            23.976: { numerator: 1001, denominator: 24000 },
            29.97: { numerator: 1001, denominator: 30000 },
            59.94: { numerator: 1001, denominator: 60000 }
        };
        const fractionalRate = fractionalFrameRateMap[frameRate];
        if (fractionalRate) {
            return `${totalFrames * fractionalRate.numerator}/${fractionalRate.denominator}s`;
        }
        return `${totalFrames}/${Math.round(frameRate)}s`;
    },

    /**
     * Convert timecode string back to seconds
     * @param {String} timecode - Timecode in HH:MM:SS:FF format
     * @param {Number} frameRate - Frame rate for calculation
     * @param {Boolean} dropFrame - Whether timecode uses drop-frame format
     * @returns {Number} Time in seconds
     */
    convertTimecodeToSeconds: function(timecode, frameRate, dropFrame = false) {
        frameRate = frameRate || 25;
        
        const parts = timecode.split(':');
        if (parts.length !== 4) {
            return 0;
        }
        
        const hours = parseInt(parts[0], 10) || 0;
        const minutes = parseInt(parts[1], 10) || 0;
        const seconds = parseInt(parts[2], 10) || 0;
        const frames = parseInt(parts[3], 10) || 0;
        
        // Convert to total frames first
        let totalFrames = frames;
        totalFrames += seconds * frameRate;
        totalFrames += minutes * 60 * frameRate;
        totalFrames += hours * 3600 * frameRate;
        
        // Adjust for drop-frame if needed
        if (dropFrame && (frameRate === 29.97 || frameRate === 59.94)) {
            // Drop-frame timecode skips certain frame numbers to keep timecode aligned with real time
            // For 29.97fps: skip frames 00 and 01 at the start of each minute, except every 10th minute
            // For 59.94fps: skip frames 00, 01, 02, and 03 at the start of each minute, except every 10th minute
            const dropFrames = frameRate === 59.94 ? 4 : 2;
            const totalMinutes = hours * 60 + minutes;
            
            // Calculate how many frames were dropped (skipped) in the timecode
            // Every 10 minutes has 9 drops (minutes 1-9, but not minute 0)
            const tenMinuteGroups = Math.floor(totalMinutes / 10);
            const remainingMinutes = totalMinutes % 10;
            
            // Count drops in remaining minutes within the current 10-minute group
            // Minutes 1-9 drop frames, but minute 0 does not
            let remainingDrops = 0;
            if (remainingMinutes > 0) {
                remainingDrops = remainingMinutes * dropFrames;
            }
            
            // Total dropped frames = (9 drops per 10-minute group) + (drops in remaining minutes)
            const droppedFrames = (tenMinuteGroups * 9 * dropFrames) + remainingDrops;
            
            // Add the dropped frames back to get the actual frame count
            totalFrames += droppedFrames;
        }
        
        return totalFrames / frameRate;
    },

    /**
     * Get frame count from seconds and frame rate
     * @param {Number} seconds - Time in seconds
     * @param {Number} frameRate - Frame rate
     * @returns {Number} Total frame count
     */
    getFrameCount: function(seconds, frameRate) {
        frameRate = frameRate || 25;
        return Math.round(seconds * frameRate);
    },

    /**
     * Convert frame count to seconds
     * @param {Number} frames - Frame count
     * @param {Number} frameRate - Frame rate
     * @returns {Number} Time in seconds
     */
    convertFramesToSeconds: function(frames, frameRate) {
        frameRate = frameRate || 25;
        return frames / frameRate;
    },

    /**
     * Determine if frame rate should use drop-frame timecode
     * @param {Number} frameRate - Frame rate to check
     * @returns {Boolean} True if drop-frame should be used
     */
    shouldUseDropFrame: function(frameRate) {
        return frameRate === 29.97 || frameRate === 59.94;
    },

    /**
     * Get standard frame rates with their properties
     * @returns {Array} Array of frame rate objects with properties
     */
    getStandardFrameRates: function() {
        return [
            { rate: 23.976, name: '23.976 fps', dropFrame: false, description: 'Film standard' },
            { rate: 24, name: '24 fps', dropFrame: false, description: 'Cinema standard' },
            { rate: 25, name: '25 fps', dropFrame: false, description: 'PAL standard' },
            { rate: 29.97, name: '29.97 fps', dropFrame: true, description: 'NTSC standard' },
            { rate: 30, name: '30 fps', dropFrame: false, description: 'Non-drop 30fps' },
            { rate: 59.94, name: '59.94 fps', dropFrame: true, description: 'NTSC 60p' },
            { rate: 60, name: '60 fps', dropFrame: false, description: 'Progressive 60fps' }
        ];
    },

    /**
     * Validate frame rate
     * @param {Number} frameRate - Frame rate to validate
     * @returns {Boolean} True if frame rate is valid
     */
    isValidFrameRate: function(frameRate) {
        return frameRate > 0 && frameRate <= 120;
    },

    /**
     * Get the closest standard frame rate
     * @param {Number} frameRate - Input frame rate
     * @returns {Number} Closest standard frame rate
     */
    getClosestStandardFrameRate: function(frameRate) {
        const standardRates = this.getStandardFrameRates().map(fr => fr.rate);
        
        let closest = standardRates[0];
        let minDiff = Math.abs(frameRate - closest);
        
        for (const rate of standardRates) {
            const diff = Math.abs(frameRate - rate);
            if (diff < minDiff) {
                minDiff = diff;
                closest = rate;
            }
        }
        
        return closest;
    },

    /**
     * Calculate duration between two timecodes
     * @param {String} startTimecode - Start timecode (HH:MM:SS:FF)
     * @param {String} endTimecode - End timecode (HH:MM:SS:FF)
     * @param {Number} frameRate - Frame rate for calculation
     * @returns {Number} Duration in seconds
     */
    calculateDuration: function(startTimecode, endTimecode, frameRate) {
        const startSeconds = this.convertTimecodeToSeconds(startTimecode, frameRate);
        const endSeconds = this.convertTimecodeToSeconds(endTimecode, frameRate);
        return Math.max(0, endSeconds - startSeconds);
    },

    /**
     * Add offset to timecode
     * @param {String} timecode - Original timecode (HH:MM:SS:FF)
     * @param {Number} offsetSeconds - Offset in seconds (can be negative)
     * @param {Number} frameRate - Frame rate for calculation
     * @returns {String} New timecode with offset applied
     */
    addOffsetToTimecode: function(timecode, offsetSeconds, frameRate) {
        const originalSeconds = this.convertTimecodeToSeconds(timecode, frameRate);
        const newSeconds = Math.max(0, originalSeconds + offsetSeconds);
        return this.convertSecondsToTimecode(newSeconds, frameRate);
    }

};

export default TimecodeConverter;
